import React from 'react';
import { Alert, Box, SpaceBetween, StatusIndicator, Table } from '@amzn/awsui-components-react';
import { BlockedItem, BulkExecutionCounts, BulkExecutionResult, BulkPreflightResult, ItemOutcome } from 'src/models/promotion/BulkPromotionTypes';
import { BulkPromoteDisposition } from 'src/models/promotion/bulkPromotionEnums';
import { PromotionComponentType } from 'src/models/promotion/PromotionTypes';
import CopyToClipboard from 'src/components/CopyToClipboard';
import { getBatchStatusBand, getDispositionBand, isFailureReason } from './bulkPromotionBands';
import { formatIdsForClipboard } from './bulkIdentifierClientParser';
import { isVerdictDependencyUnverified } from './PreflightSummary';
import PrerequisiteList from './PrerequisiteList';
import { BulkPromotionContext } from 'src/services/bulk-promotion/BulkPromotionContext';

/**
 * Wave recorded on a row that was derived from the verdict rather than reported by the service.
 *
 * A component refused at preflight never entered a wave, so any number here would be invented. Zero is
 * rendered as an em dash rather than as "0", which would read as a real wave.
 */
const NO_WAVE = 0;

/** The split of everything that did not promote. Blocked needs an operator; skipped does not. */
export interface UnpromotedSplit {
    skipped: number;
    blocked: number;
}

/**
 * How many were skipped versus genuinely blocked.
 *
 * The service's own counters win whenever they carry a skipped count, and the rest of this exists because
 * today they do not. `BulkPromotionCounts` has no skipped member, and its `blocked` counts every `BLOCKED#`
 * row, lumping "already identical in the target" in with "a dependency is missing". Reported straight
 * through, a converged re-run reads as blocked, contradicting the sentence printed directly beneath.
 *
 * A skipped count of zero is therefore treated as absent rather than as an answer: zero is exactly what a
 * payload without the counter looks like, and it is the wrong answer in the case being fixed. Any non-zero
 * value can only have come from a payload that really does carry one, so it is trusted.
 *
 * The fallback reads the verdict, which holds the honest split: the service reports the unchanged set apart
 * from the refused set, so the confirmation screen and these counters would otherwise disagree.
 *
 * Any excess of the server's total over the verdict's is added to blocked, never to skipped. A component
 * refused at revalidation was blocked after the verdict was produced, so it is missing from the verdict but
 * is still a block; attributing it to skipped would report a refusal as nothing-to-do.
 */
export function splitUnpromoted(
    preflight: BulkPreflightResult | null,
    counts: BulkExecutionCounts,
): UnpromotedSplit | null {
    if (counts.skipped) {
        return { skipped: counts.skipped, blocked: counts.blocked };
    }
    if (!preflight) {
        // A batch recovered from history without its verdict. The split exists nowhere the client can read,
        // and inventing one would be worse than declining to show it.
        return null;
    }
    const skipped = (preflight.skipped || []).length;
    // The summary's count over the row count, for the same reason PreflightSummary prefers it: rows arrive
    // in best-effort frames and a lost frame would under-report.
    const blockedAtPreflight = preflight.blockedCount ?? (preflight.blocked || []).length;
    return { skipped, blocked: Math.max(blockedAtPreflight, counts.blocked - skipped) };
}

/**
 * One outcome row per component that did not promote, built from the verdict.
 *
 * The service populates no per-item outcomes at all — `perItem` is absent from `BulkExecutionResult` in the
 * model, so the table it feeds has always rendered empty. The verdict, however, already carries every
 * refused component with its reason, detail and blocking dependency: the same `BLOCKED#` rows, already sent
 * to this page and already displayed one screen earlier. So this re-presents them rather than fetching
 * anything.
 *
 * Promoted components are deliberately absent. The client holds no promoted-id list at this point —
 * `promotedIds` exists only on the `GetBulkPromotion` detail, which this flow never calls — and a row per
 * success is not what the table is read for. The header says "Not promoted" so the omission is stated
 * rather than implied.
 */
export function deriveOutcomesFromVerdict(preflight: BulkPreflightResult | null): ItemOutcome[] {
    if (!preflight) {
        return [];
    }
    const rows: ItemOutcome[] = (preflight.skipped || []).map((componentId) => ({
        componentId,
        disposition: BulkPromoteDisposition.SKIPPED_UNCHANGED,
        reason: 'Already up to date: promoted before and unchanged since.',
        missingDependencies: [],
        wave: NO_WAVE,
    }));
    // Defaulted like the adjacent skipped list: blocked is not @required on BulkPreflightResult, and an
    // absent list threw inside render — the failure this file documents as leaving the operator a blank page.
    for (const item of preflight.blocked || []) {
        rows.push({
            componentId: item.componentId,
            // UNCHANGED_SINCE_LAST_PROMOTION can appear in this list on a recovered batch record, so the
            // non-failure rule is applied here too rather than assuming the service pre-sorted it out.
            disposition: isFailureReason(item.reason)
                ? BulkPromoteDisposition.BLOCKED_AT_PREFLIGHT
                : BulkPromoteDisposition.SKIPPED_UNCHANGED,
            reason: item.detail,
            missingDependencies: blockersOf(item),
            wave: NO_WAVE,
        });
    }
    return rows;
}

/*
 * The blocking dependency, as the verdict names it.
 *
 * Reads missingDependency only. This used to prefer a `namedMaster` field first, which no producer sets:
 * the Smithy BlockedItem declares componentId, reason, missingDependency and detail and nothing else, the
 * asynchronous report frame carries no such member either, and the service has no writer for it — so the
 * preference was dead on every path and only made the cell look conditional.
 */
function blockersOf(item: BlockedItem): string[] {
    const blocker = item.missingDependency;
    return blocker ? [blocker] : [];
}

/**
 * Ids of every item that did not promote, for a corrective re-run.
 *
 * Tolerates a missing list: `perItem` has no `@required` in bulk-promotion-shapes.smithy, so a
 * payload can reach here without one even though the TypeScript type says otherwise. Reading it
 * unguarded threw inside render, which unmounts the tree and leaves the operator a blank page while
 * the batch promotes in the background.
 */
export function getUnpromotedIds(perItem: ItemOutcome[] | undefined): string[] {
    return (perItem ?? [])
        .filter((outcome) => outcome.disposition !== BulkPromoteDisposition.INITIATED)
        .map((outcome) => outcome.componentId);
}

/** Per-item outcomes for a finished batch. */
export default function BulkPromoteResults() {
    const { bulkPromotionState } = React.useContext(BulkPromotionContext);
    const componentType = bulkPromotionState.componentType as PromotionComponentType;
    const execution = bulkPromotionState.execution as BulkExecutionResult;
    if (!execution) {
        return null;
    }
    /*
     * Defaulted: `counts` is not @required on the execute acknowledgement, and the push path dispatches the
     * raw wire object, so an omitted counter reached `counts.skipped` and threw inside render.
     */
    const counts = execution.counts || { requested: 0, promoted: 0, blocked: 0, failed: 0, skipped: 0 };
    const preflight = bulkPromotionState.preflight as BulkPreflightResult | null;

    const reported = execution.perItem ?? [];
    // The service's own rows win when it sends any. The verdict is the fallback, not an override.
    const derived = reported.length === 0;
    const perItem = derived ? deriveOutcomesFromVerdict(preflight) : reported;
    const unpromoted = getUnpromotedIds(perItem);
    const waves = new Set(perItem.filter((outcome) => outcome.wave !== NO_WAVE).map((outcome) => outcome.wave));
    const split = splitUnpromoted(preflight, counts);

    return (
        <SpaceBetween direction="vertical" size="l">
            {isVerdictDependencyUnverified(execution.validationMode) && (
                <Alert type="warning" header="Dependencies were not fully checked for this batch">
                    This batch was not validated under a full parity check, so a dependency may be present in the
                    target without matching the source. Treat it as unverified rather than as a clean pass.
                </Alert>
            )}
            <Box>
                <Box variant="h3">Batch {execution.batchId}</Box>
                {/*
                  * The terminal outcome, stated. The reducer forces phase COMPLETE on every terminal, so the
                  * only banded headline on this screen used to read a green "Complete" whether the batch
                  * SUCCEEDED, ended PARTIALLY_SUCCEEDED, FAILED, or was ABANDONED with nothing attempted —
                  * 9 of 49 batches in alpha. getBatchStatusBand already carried the right prose for each,
                  * including a distinct label for ABANDONED, and no component read it.
                  */}
                {execution.status && (
                    <Box variant="p">
                        <StatusIndicator type={getBatchStatusBand(execution.status)[1]}>
                            {getBatchStatusBand(execution.status)[0]}
                        </StatusIndicator>
                    </Box>
                )}
                <Box variant="p">
                    {counts.requested} requested · {counts.promoted} promoted ·{' '}
                    {split ? (
                        <>
                            {split.skipped} skipped · {split.blocked} blocked
                        </>
                    ) : (
                        <>{counts.blocked} not promoted</>
                    )}{' '}
                    · {counts.failed} failed
                </Box>
                <Box variant="small">
                    Skipped components are not failures — they are already identical in the target, so they were
                    not promoted again.
                </Box>
            </Box>
            <Table
                variant="embedded"
                trackBy="componentId"
                items={perItem}
                columnDefinitions={[
                    {
                        id: 'componentId',
                        header: 'Component',
                        cell: (item: ItemOutcome) => item.componentId,
                        isRowHeader: true,
                    },
                    {
                        id: 'disposition',
                        header: 'Outcome',
                        cell: (item: ItemOutcome) => {
                            const [label, band] = getDispositionBand(item.disposition);
                            return <StatusIndicator type={band}>{label}</StatusIndicator>;
                        },
                    },
                    {
                        id: 'reason',
                        header: 'Detail',
                        cell: (item: ItemOutcome) => item.reason || '',
                    },
                    {
                        id: 'missingDependencies',
                        header: 'Missing dependencies',
                        cell: (item: ItemOutcome) => (item.missingDependencies || []).join(', '),
                    },
                    {
                        // Surfaced so a two-wave run reads as one batch that went in order rather than as two
                        // unrelated runs. A row derived from the verdict never entered a wave, so it shows none.
                        id: 'wave',
                        header: 'Wave',
                        cell: (item: ItemOutcome) => (item.wave === NO_WAVE ? '—' : item.wave),
                    },
                ]}
                header={
                    <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="h4">
                            {derived ? 'Not promoted' : 'Outcomes'} ({perItem.length}
                            {waves.size > 1 ? `, ${waves.size} waves` : ''})
                        </Box>
                        {unpromoted.length > 0 && (
                            <CopyToClipboard
                                textToBeCopied={formatIdsForClipboard(unpromoted, componentType)}
                                popoverMessage={`${unpromoted.length} IDs copied`}
                            />
                        )}
                    </SpaceBetween>
                }
                empty={<Box textAlign="center">No outcomes recorded.</Box>}
            />
            <PrerequisiteList prerequisites={execution.prerequisites} />
        </SpaceBetween>
    );
}
